import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Terminal, CreditCard, Users, Settings, Package } from 'lucide-react';

const navItems = [
  { label: 'Home', path: '/', icon: Home },
  { label: 'Command', path: '/command-center-ai-execution-interface', icon: Terminal },
  { label: 'LEADS', path: '/lead-command-center', icon: Users },
  { label: 'DELIVERY', path: '/delivery-command-center', icon: Package },
  { label: 'OPERATIONS', path: '/operations', icon: Settings },
  { label: 'Pricing', path: '/pricing', icon: CreditCard },
];

const GlobalNavMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const hiddenPaths = ['/user-login', '/user-registration'];
  if (hiddenPaths?.includes(location?.pathname)) return null;

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-6 left-4 z-[9999] flex flex-col items-start gap-2">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            style={{
              background: '#0D0D1A',
              border: '1px solid rgba(0,255,229,0.4)',
              borderRadius: '4px',
              boxShadow: '0 0 30px rgba(0,255,229,0.1)',
            }}
            className="overflow-hidden w-52"
          >
            {/* Header */}
            <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(0,255,229,0.2)' }}>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(0,255,229,0.6)', fontFamily: "'JetBrains Mono', monospace" }}>NAVIGATE</span>
            </div>
            {/* Nav Items */}
            <div className="py-2">
              {navItems?.map((item) => {
                const ItemIcon = item?.icon;
                const isActive = location?.pathname === item?.path || (item?.path === '/' && location?.pathname === '/landing-page-ai-execution-infrastructure');
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigate(item?.path)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150"
                    style={{
                      color: isActive ? '#00FFE5' : 'rgba(0,255,229,0.6)',
                      background: isActive ? 'rgba(0,255,229,0.08)' : 'transparent',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.75rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#00FFE5'; e.currentTarget.style.background = 'rgba(0,255,229,0.05)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'rgba(0,255,229,0.6)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{item?.label}</span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#00FFE5', boxShadow: '0 0 6px #00FFE5' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '40px',
          height: '40px',
          background: '#0D0D1A',
          border: '1px solid rgba(0,255,229,0.3)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#00FFE5',
          boxShadow: '0 0 15px rgba(0,255,229,0.1)',
        }}
        aria-label="Toggle navigation menu"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="w-4 h-4" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <Menu className="w-4 h-4" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default GlobalNavMenu;
